import Log from '@/Logger';
import { createDocumentUrl, kintoneAPIWrapper } from './api';

/**
 * kintone アプリのレイアウトやフィールド情報を含む JSON の型(内部は横流しするだけでパースしないので内部は定義しない)
 */
type KintoneData = {
    formLayout: string, // フォームレイアウトのデータ型
    fieldInfo: string;  // フィールド情報のデータ型
}

/**
 * Lambda関数に送る引数の型
 */
export interface PDFSender {
    /** 生成されるファイル名 (kintoneアプリ名 + 現在時刻 + '.pdf') */
    fileName: string;
    /** kintone のレイアウトやフィールド情報 */
    kintone: KintoneData;
    /** デバイス ID */
    deviceId: string;
    /** 認証用のアクセストークン */
    accessToken: string;
}

export async function sendDocument(deviceId: string, file: File, setIsloading: React.Dispatch<React.SetStateAction<boolean>>): Promise<Response | any> {
    const logger = Log.getLogger();
    setIsloading(true);
    try {
        const apiResponse = await createDocumentUrl(deviceId, file.name, 'application/pdf');

        logger.api.info('Lantana-API', 'Start to request: createDocumentUrl:upload');
        const uploadResponse = await fetch(apiResponse.url, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type,
            },
        });
        logger.api.info('Lantana-API', 'Succeed to request: createDocumentUrl:upload');
        setIsloading(false);
        return uploadResponse;
    } catch (error) {
        setIsloading(false);
        throw error;
    }
};

export function isValidType(file: File): boolean {
    const splittedFileName = file.name.split('.');
    return splittedFileName.length < 2 || splittedFileName.pop()?.toLowerCase() !== 'pdf' ? false : true;
};

export function isValidSize(file: File): boolean {
    return file.size > 10485760 ? false : true;
};

//iso8601形式の文字列を受け取り、現在より過去かどうかを判定する
//過去(期限切れ)ならtrueを返す
export function isExpired(expirationDate: string): boolean {
    const now = new Date();
    const expiration = new Date(expirationDate);
    return expiration <= now;
};

/**
 * appidを受け取り、レイアウトとアプリ情報をリクエストする。
 * それらをkintone-formpdfsenderのlambdaのペイロードに合わせた形に整形する
 */
export async function generateKintonePdfSenderPayload(appId: number, appName: string, deviceId: string, accessToken: string): Promise<PDFSender> {
    const layoutJson = await getKintoneAppLayout(appId)
    const fieldJson = await getKintoneAppFieldInfo(appId)

    //現在時刻を'199811012100'形式の文字列で取得
    const createdTime = getFormattedCurrentTime()

    const mergedJson: PDFSender = {
        //デバイスに送られるファイル名はkintoneアプリ名に現在時刻を加えたもの
        fileName: appName + '_' + createdTime + '.pdf',
        kintone: {
            formLayout: layoutJson,
            fieldInfo: fieldJson,
        },
        deviceId,
        accessToken,
    };
    return mergedJson
}

//指定したアプリIDのレイアウトを取得する
async function getKintoneAppLayout(kintoneAppId: number): Promise<any> {
    const appLayout = await kintoneAPIWrapper('get-form-layout', JSON.stringify({ app: kintoneAppId }))
    return JSON.parse(appLayout.kintoneResponse)
}

//指定したアプリIDのフィールド情報を取得する
async function getKintoneAppFieldInfo(kintoneAppId: number): Promise<any> {
    const appFieldInfo = await kintoneAPIWrapper('get-form-fields', JSON.stringify({ app: kintoneAppId }))
    return JSON.parse(appFieldInfo.kintoneResponse)
}

function getFormattedCurrentTime(): string {
    const now = new Date();

    //ローカルタイムゾーンで時間を取得
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    //'199811012100'形式で返す
    return `${year}${month}${day}${hours}${minutes}`;
}
